import { BasePayloadMapper } from '@ps/utils';

import {
  AnswerOption,
  DataQuality,
  Dependency,
  DynamicProfiler,
  Localization,
  Segmentation,
} from '../../models/localization.model';
import {
  AnswerOptionDto,
  DataQualityDto,
  DependencyDto,
  DynamicProfilerDto,
  LocalizationDto,
  SegmentationDto,
} from '../dtos/localization.dto';

export class LocalizationPayloadMapper extends BasePayloadMapper<
  Localization,
  LocalizationDto
> {
  public toPayload(model: Localization): LocalizationDto {
    const isEnglish = this._isEnglishLocale(model.locale);

    return {
      locale: model.locale,
      text: model.text,
      placeholder: model.placeholder,
      hasDependency: model.hasDependency,
      ...(model.hasDependency &&
        model.dependency && {
          dependency: this._toDependencyDto(model.dependency),
        }),
      hasSegmentation: model.hasSegmentation,
      ...(model.hasSegmentation &&
        model.segmentation && {
          segmentation: this._toSegmentationDto(model.segmentation),
        }),
      ...(model.dataQuality && {
        data_quality: this._toDataQualityDto(model.dataQuality),
      }),
      dynamic_profiler: this._toDynamicProfilerDto(model.dynamicProfiler),
      non_gdpr: model.nonGdpr,
      pp_text: model.ppText,
      pp_modal_text: model.ppModalText,
      create_survey_position: model.createSurveyPosition,
      create_survey_component: model.createSurveyComponent,
      name_alias: model.nameAlias,
      has_census_data: model.hasCensusData,
      answers: model.answers.map((answer) =>
        this._toAnswerDto(answer, isEnglish),
      ),
    };
  }

  private _isEnglishLocale(locale: string): boolean {
    return locale.split('_')[0] === 'en';
  }

  private _toDependencyDto(model: Dependency): DependencyDto {
    return {
      parent_qualification_id: model.parentQualificationId,
      match_criteria: model.matchCriteria,
      condition: model.condition,
    };
  }

  private _toSegmentationDto(model: Segmentation): SegmentationDto {
    return {
      parent_qualification_id: model.parentQualificationId,
      child_qualification_id: model.childQualificationId,
      condition: model.condition,
    };
  }

  private _toDataQualityDto(model: DataQuality): DataQualityDto {
    return {
      enable: model.enable,
      eligible_for_oe_termination: model.eligibleForOeTermination,
      quality_rules: model.qualityRules,
    };
  }

  private _toDynamicProfilerDto(model: DynamicProfiler): DynamicProfilerDto {
    return {
      enable: model.enable,
      priority: model.priority,
    };
  }

  private _toAnswerDto(
    model: AnswerOption,
    isEnglish: boolean,
  ): AnswerOptionDto {
    return {
      answer_id: String(model.id),
      condition_code: model.id,
      eng_text: isEnglish ? model.text : model.translation,
      local_text: model.text,
    };
  }
}
